import React, { useEffect, useMemo, useState } from "react";

import {
    DiaryCategory,
    DiaryItem,
    DiaryItemStatus,
    DiaryItemType,
    DiaryVisibility,
    DiaryItemUpsertDto,
    DiaryItemDto,
    DiaryCreatedSource,
} from "../../models/diaryTypes";

import DiaryItemModal, { ViewMode } from "./DiaryItemModal";
import DiaryChildModal from './DiaryChildModal';
import { DiaryEntriesList, EntryAction, DiaryView } from './DiaryEntriesList';
import DiaryFilterControl,{ DiaryFilters } from './DiaryFilterControls';

import { useApiClient } from '../../components/useApiClient';
import { useUser } from '../../components/UserContext';
import useAuthToken from '../../functions/useAuthToken';
import { ClientUser } from "../../models/UserProfile";

import { toIsoUtc, startOfTodayUtc, startOfDayUtc, endOfDayUtc, addDays } from "../../functions/dateFormatter";

import { defaultViewFilters, FilterItem } from './ViewFilterSelector';
export default function DiaryPage() {

    const { activeClientId, isAdmin, isOwner, isValuer, activeClientLicense } = useUser();
    const isViewOnly = !activeClientLicense || activeClientLicense?.licenseExpired;

    // Diary Management APIs
    const getAccessToken = useAuthToken();
    const { get, put, putJson, post, postJson, delete: del } = useApiClient();

    async function getDiaryRange(fromUtc: string, toUtc: string, category?: number, status?: number) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in getDiaryRange');

            const qs = new URLSearchParams({ fromUtc, toUtc });
            if (category) qs.set("category", String(category));
            if (status) qs.set("status", String(status));

            const res = await get <DiaryItem[]>(`/diary?${qs.toString()}`,accessToken, );
            return res;
        }
        catch (e) {
            console.error('Error in getDiaryRange', e);
            throw new Error('Error in getDiaryRange');
        }
    }

    async function createDiaryItem(dto: DiaryItemUpsertDto) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in createDiaryItem');

            const res = await postJson(`diary`, accessToken, JSON.stringify(dto))

            return (res) as { id: number };
        }
        catch (e) {
            console.error('Error in createDiaryItem', e);
            throw new Error('Error in createDiaryItem');
        }
    }

    async function updateDiaryItem(id: number, dto: DiaryItemUpsertDto) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in updateDiaryItem');

            const res = await putJson(`diary/${id}`, accessToken, JSON.stringify(dto));            
        }
        catch (e) {
            console.error('Error in updateDiaryItem', e);
            throw new Error('Error in updateDiaryItem');
        }
    }

    async function completeDiaryItem(id: number) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in completeDiaryItem');

            const formData = new FormData();
            const res = await post(`diary/${id}/complete`, accessToken, formData);
        }
        catch (e) {
            console.error('Error in completeDiaryItem', e);
            throw new Error('Error in completeDiaryItem');
        }
    }

    async function cancelDiaryItem(id: number) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in cancelDiaryItem');

            const formData = new FormData();
            const res = await post(`diary/${id}/cancel`, accessToken, formData);
        }
        catch (e) {
            console.error('Error in cancelDiaryItem', e);
            throw new Error('Error in cancelDiaryItem');
        }
    }

    async function getDiaryItemById(id: number, includeChildren: boolean) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in getDiaryRange');

            const qs = includeChildren ? "?includeChildren=true" : "";

            const res = await get<DiaryItemDto>(`diary/${id}${qs}`, accessToken,);
            return res;
        }
        catch (e) {
            console.error('Error in getDiaryItemById', e);
            throw new Error('Error in getDiaryItemById');
        }
    }

    async function refreshParent(id: number) {
        const refreshed = await getDiaryItemById(id, true);
        setEditingParent(refreshed);
        return refreshed;
    }

    async function regenerateSeries(id: number, fromUtc?: string, toUtc?: string) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in createDiaryItem');

            const res = await postJson(`diary/${id}/regenerate`, accessToken, JSON.stringify({ fromUtc, toUtc }))

            return (res);
        }
        catch (e) {
            console.error('Error in regenerateSeries', e);
            throw new Error('Error in regenerateSeries');
        }
    }

    async function createChildDiaryItem(parentId: number, dto: DiaryItemUpsertDto) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in createDiaryItem');

            const res = await postJson(`diary/${parentId}/children`, accessToken, JSON.stringify(dto))

            return (res);
        }
        catch (e) {
            console.error('Error in regenerateSeries', e);
            throw new Error('Error in regenerateSeries');
        }
    }


    const handleEntryAction = (action: EntryAction, data: DiaryItem | number) => {
        switch (action) {
            case 'edit':
            case 'view':
                // data is the full entry object
                onEdit(data as DiaryItem);
                break;

            case 'complete':
                // data is the entry ID
                const entryId = data as number;
                onComplete(entryId);
                break;

            case 'cancel':
                // data is the entry ID
                const cancelId = data as number;
                onCancel(cancelId);
                break;

            case 'delete':
                // data is the entry ID
                const deleteId = data as number;
                break;
        }
    };

    const handleEntryClick = (entry: DiaryItem) => {
        // Handle entry click - navigate to detail view)
        onEdit(entry);
    };

    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);

    const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);

    const [rangePreset, setRangePreset] = useState<"7" | "30" | "custom">("7");
    const [fromUtc, setFromUtc] = useState<Date>(startOfTodayUtc());
    const [toUtc, setToUtc] = useState<Date>(addDays(startOfTodayUtc(), 7));

    const [category, setCategory] = useState<number | "">("");
    const [status, setStatus] = useState<number | "">("");
    const [viewMode, setViewMode] = useState<DiaryView>('grouped');

    const [viewFilters, setViewFilters] = useState<FilterItem[]>([]);

    const [filters, setFilters] = useState<DiaryFilters>({
        category: DiaryCategory.All,
        status: DiaryItemStatus.All,
        dateRange: { start: null, end: null },
        viewFilters: defaultViewFilters,
    });

    const [items, setItems] = useState<DiaryItem[]>([]);
   /* const [filteredItems, setFilteredItems] = useState<DiaryItem[]>([]);*/
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [editingParent, setEditingParent] = useState<DiaryItemDto | null>(null);
    const [modalViewMode, setModalViewMode] = useState<ViewMode>("edit");

    //Child Modal
    const [childDialogOpen, setChildDialogOpen] = useState(false);
    const [editingChild, setEditingChild] = useState<DiaryItemDto | null>(null);
    const [isChildSaving, setIsChildSaving] = useState(false);

    async function closeChildDialog() {
        if (isChildSaving) return;
        setChildDialogOpen(false);
        setEditingChild(null);
        setEditingParent(null);
        await load();
    }

    async function getfilteredItems(diaryItems: DiaryItem[], appliedFilters: FilterItem[] ) {

        const activeFilters = appliedFilters.filter(f => f.checked);

        // If no filters are selected, return empty array
        if (activeFilters.length === 0) return [];

        // Separate type filters (tasks, events) from attribute filters (series, linked)
        const typeFilters = activeFilters.filter(f => f.category === 'type');
        const attributeFilters = activeFilters.filter(f => f.category === 'attribute');

        // If no type filters are selected but attribute filters are, show nothing
        if (typeFilters.length === 0 && attributeFilters.length > 0) {
            return [];
        }

        // If only type filters are selected (no attributes), show all items of those types
        if (attributeFilters.length === 0) {
            return diaryItems.filter(item =>
                typeFilters.some(filter => filter.filterFn(item))
            );
        }

        // Apply hierarchical filtering: attributes only filter within selected types
        return diaryItems.filter(item => {
            // First check if item matches any selected type
            const matchesType = typeFilters.some(filter => filter.filterFn(item));
            if (!matchesType) return false;

            // If no attribute filters are active for this item's type, include it
            if (attributeFilters.length === 0) return true;

            // Check if item matches any active attribute filter
            return attributeFilters.some(attrFilter => attrFilter.filterFn(item));
        });
    }

    useEffect(() => {

        if (!clientIdRef.current) return;

        const fetchData = async () => {
            (async () => {
                try {
                    const accessToken = await getAccessToken();
                    if (!accessToken) return;

                    const userList = await get<ClientUser[]>('user/list', accessToken);
                    setClientUsers(userList);

                } catch (err) {
                    console.error('Error loading user data', err);
                    setClientUsers([]);
                }
            })();
        };

        fetchData();

    }, [clientIdRef.current]);

    useEffect(() => {
        const base = startOfTodayUtc();
        if (rangePreset === "7") {
            setFromUtc(base);
            setToUtc(addDays(base, 7));
        } else if (rangePreset === "30") {
            setFromUtc(base);
            setToUtc(addDays(base, 30));
        }
    }, [rangePreset]);

    async function load() {
        setLoading(true);
        setError(null);

        try {
            const data = await getDiaryRange(
                toIsoUtc(fromUtc),
                toIsoUtc(toUtc),
                category === "" ? undefined : Number(category),
                status === "" ? undefined : Number(status)
            );
            setItems(data);

            //const filtered = await getfilteredItems(data, filters.viewFilters);
            //setFilteredItems(filtered);

        } catch (e: any) {
            setError(e?.message ?? "Failed to load diary items.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, [fromUtc, toUtc, category, status, viewFilters]);

    const filteredItems = useMemo(() => {
        const activeFilters = filters.viewFilters.filter(f => f.checked);

        // If no filters are selected, return empty array
        if (activeFilters.length === 0) return [];

        // Separate type filters (tasks, events) from attribute filters (series, linked)
        const typeFilters = activeFilters.filter(f => f.category === 'type');
        const attributeFilters = activeFilters.filter(f => f.category === 'attribute');

        // If no type filters are selected but attribute filters are, show nothing
        if (typeFilters.length === 0) {
            return [];
        }

        // ADDITIVE LOGIC:
        // 1. If only types are selected: show all items of those types (primary items)
        // 2. If types + attributes are selected: show primary items of those types + 
        //    linked items (series/linked) of those types

        return items.filter(item => {
            // Check if item matches any active type filter
            const matchesType = typeFilters.some(typeFilter => {
                if (typeFilter.id === 'tasks') return item.type === DiaryItemType.Task;
                if (typeFilter.id === 'events') return item.type === DiaryItemType.Event;
                return false;
            });

            if (!matchesType) return false;

            // Determine which attributes are active
            const hasSeries = attributeFilters.some(attr => attr.id === 'series');
            const hasLinked = attributeFilters.some(attr => attr.id === 'linked');

            // If no attribute filters are active, only show primary items
            if (!hasSeries && !hasLinked) {
                return item.parentDiaryItemId === null;
            }

            // Check if item is a primary item (no parent)
            const isPrimaryItem = item.parentDiaryItemId === null;

            // Check if item is a linked item and matches active attribute filters
            const isSeriesItem = hasSeries &&
                item.createdSource === DiaryCreatedSource.System &&
                !!item.parentDiaryItemId;

            const isLinkedItem = hasLinked &&
                item.createdSource === DiaryCreatedSource.User &&
                !!item.parentDiaryItemId;

            // Return true if item is:
            // 1. A primary item of selected type, OR
            // 2. A linked item (series/linked) of selected type AND matching active attributes
            return isPrimaryItem || isSeriesItem || isLinkedItem;
        });
    }, [items]);

    async function handleFilterChange(updatedFilter: DiaryFilters) {
        setFilters(updatedFilter);

        // Date range filter
        var startTime = startOfTodayUtc();
        var endTime = addDays(startOfTodayUtc(), 7);

        const startDateSet = (updatedFilter?.dateRange?.start);
        const endDateSet = (updatedFilter?.dateRange?.end);

        if (updatedFilter?.dateRange?.start) {
            startTime = startOfDayUtc(new Date(updatedFilter.dateRange.start));

            if (!endDateSet) { endTime = addDays(startTime, 90) }
        }

        if (updatedFilter?.dateRange?.end) {
            endTime = endOfDayUtc(new Date(updatedFilter.dateRange.end));

            if (!startDateSet) { startTime = addDays(endTime, -90) }
        }

        //Apply Date Filters
        if (fromUtc != startTime) { setFromUtc(startTime); }
        if (toUtc != endTime) { setToUtc(endTime); }
        
        //Category Filter
        const newCategory = updatedFilter.category ?? "";
        if (category != newCategory) { setCategory(newCategory);}

        //Status Filter
        const newStatus = updatedFilter.status ?? "";
        if (status != newStatus) { setStatus(newStatus); }

        setViewFilters(updatedFilter.viewFilters);

        await load()
    }

    function onAdd(type: DiaryItemType) {
        setEditingParent(createBlank(type));
        setModalViewMode("add");
        setModalOpen(true);
    }

    async function onEdit(item: DiaryItem) {
        const full = await getDiaryItemById(item.id, true);
        setModalViewMode("edit");

        if (item.parentDiaryItemId == null) {
            //Is Parent Item
            setEditingParent(full);
            setModalOpen(true);
        } else {
            //is Child Item
            const parent = await getDiaryItemById(item.id, false);
            setEditingParent(parent);
            setEditingChild(full);
            setChildDialogOpen(true);
        }
        
    }

    async function onParentEdit(item: DiaryItem) {
        
        if (item.parentDiaryItemId != null) {
            const parent = await getDiaryItemById(item.parentDiaryItemId, true);
            setModalViewMode("edit");
            setEditingParent(parent);
            setModalOpen(true);
        }
    }

    async function onSave(dto: DiaryItemUpsertDto, id?: number) {
        if (!id) {
            await createDiaryItem(dto);
        } else {
            await updateDiaryItem(id, dto);
        }
        setModalOpen(false);
        setEditingParent(null);
        setEditingChild(null);
        await load();
    }

    async function onComplete(id: number) {
        await completeDiaryItem(id);
        await load();
    }

    async function onCancel(id: number) {
        await cancelDiaryItem(id);
        await load();
    }

    async function handleClose() {
        setModalOpen(false);
        setEditingParent(null);
        setEditingChild(null);
        await load();
    }



    return (
        <div style={{ padding: 16, maxWidth: 1100, margin: "0 auto" }}>
            <h2 style={{ marginTop: 0 }}>Diary</h2>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end", marginBottom: 12 }}>

                {/* Filter controls */}
                <DiaryFilterControl
                    filters={filters}
                    onFiltersChange={handleFilterChange}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                />

                {!isViewOnly &&
                    <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                        <button onClick={() => onAdd(DiaryItemType.Event)}>Add Entry</button>
                    </div>
                }
            </div>

            {error && <div style={{ color: "crimson", marginBottom: 8 }}>{error}</div>}
            {loading && <div>Loading…</div>}

            {!loading && filteredItems.length === 0 && <div>No diary items in this range.</div>}

            {!loading && filteredItems.length !== 0 &&
                <DiaryEntriesList entries={filteredItems}
                onEntryClick={handleEntryClick}
                onAction={handleEntryAction}
                onParentEdit={onParentEdit}
                mode={viewMode}
                showActions={!isViewOnly}
            />}

            {editingParent &&
                <DiaryItemModal
                    open={modalOpen}
                    item={editingParent}
                    viewMode={modalViewMode}
                    clientUsers={clientUsers}
                    onClose={handleClose}
                    onSave={onSave}
                    refreshParent={refreshParent}
                    regenerateSeries={regenerateSeries}
                    updateDiaryItem={updateDiaryItem}
                    createChildDiaryItem={createChildDiaryItem}
                />
            }

            {editingParent && editingChild &&
                <DiaryChildModal
                    open={childDialogOpen}
                    parent={editingParent}
                    child={editingChild}
                    onClose={closeChildDialog}
                    onSaved={refreshParent}
                    updateDiaryItem={updateDiaryItem}
                    createChildDiaryItem={createChildDiaryItem}
                />
            }
        </div>
    );
}

// ----- helpers -----

function createBlank(type: DiaryItemType): DiaryItemDto {
    const now = new Date();
    const iso = now.toISOString();
    return {
        id: 0,
        clientId: 0,
        title: "",
        description: "",
        type,
        status: DiaryItemStatus.Planned,
        category: DiaryCategory.General,
        visibility: DiaryVisibility.Private,
        startUtc: type === DiaryItemType.Event ? iso : null,
        endUtc: type === DiaryItemType.Event ? iso : null,
        dueUtc: type === DiaryItemType.Task ? iso : null,
        allDay: false,
        timezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        shares: [],
        reminders: [],
        links: [],
        createdUtc: iso,
        updatedUtc: iso,
        createdSource: DiaryCreatedSource.User,
        children: [],
        dueUtcStr: "",
        startUtcStr: "",
        endUtcStr: ""
    };
}

function formatWhen(it: DiaryItem) {
    const dt = it.type === DiaryItemType.Task ? it.dueUtc : it.startUtc;
    if (!dt) return "—";
    const d = new Date(dt);
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function groupByDay(items: DiaryItem[]) {
    const sorted = [...items].sort((a, b) => {
        const da = new Date((a.type === DiaryItemType.Task ? a.dueUtc : a.startUtc) ?? a.createdUtc).getTime();
        const db = new Date((b.type === DiaryItemType.Task ? b.dueUtc : b.startUtc) ?? b.createdUtc).getTime();
        return da - db;
    });

    const map = new Map<string, DiaryItem[]>();
    for (const it of sorted) {
        const dt = it.type === DiaryItemType.Task ? it.dueUtc : it.startUtc;
        const d = dt ? new Date(dt) : new Date(it.createdUtc);
        const dayKey = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
        if (!map.has(dayKey)) map.set(dayKey, []);
        map.get(dayKey)!.push(it);
    }

    return Array.from(map.entries()).map(([dayKey, dayItems]) => {
        const first = new Date((dayItems[0].type === DiaryItemType.Task ? dayItems[0].dueUtc : dayItems[0].startUtc) ?? dayItems[0].createdUtc);
        return {
            dayKey,
            label: first.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
            items: dayItems,
        };
    });
}
